'use client';

import React, { useState, useMemo } from 'react';
import { ethers } from 'ethers';
import { toast } from 'react-toastify';
import Wrapper from '@/layouts/Wrapper';
import { Web3ModalProvider, useWallet } from '@/hooks/Web3';
import { useLanguage } from '@/utils/LanguageContext';
import { useSwitchChain } from 'wagmi';

const CONTRACT_ADDRESS = process.env.NEXT_PUBLIC_CONTRACT_ADDRESS ?? '';
const DESIRED_CHAIN_ID = parseInt(process.env.NEXT_PUBLIC_CHAIN_ID || '1', 10);
const ABI = [
  'function addRound(uint64,uint64,uint128,uint64,uint64,uint64,uint128,uint128,uint8)',
  'function updateKycStatus(address[] users,bool status)'
] as const;

interface AddRoundContract extends ethers.BaseContract {
  addRound(
    startTime: number,
    endTime: number,
    tokensPerUsdt: bigint,
    vestingStartTime: number,
    vestingCliffSec: bigint,
    vestingDurationSec: bigint,
    allocUnits: bigint,
    nonKycUnits: bigint,
    affiliatePercent: number
  ): Promise<ethers.ContractTransactionResponse>;
  updateKycStatus(
    users: string[],
    status: boolean
  ): Promise<ethers.ContractTransactionResponse>;
}

function AddRoundPageContent() {
  const { address, signer, connect, status, chainId } = useWallet();
  const connecting = status === 'connecting' || status === 'reconnecting';
  const { t } = useLanguage();
  const { switchChain } = useSwitchChain();
  const contract = useMemo<AddRoundContract | null>(
    () => (signer ? (new ethers.Contract(CONTRACT_ADDRESS, ABI, signer) as unknown as AddRoundContract) : null),
    [signer]
  );

  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [price, setPrice] = useState('');
  const [alloc, setAlloc] = useState('');
  const [nonKyc, setNonKyc] = useState('');
  const [vestingStart, setVestingStart] = useState('');
  const [cliff, setCliff] = useState('');
  const [duration, setDuration] = useState('');
  const [affiliate, setAffiliate] = useState('');
  const [loading, setLoading] = useState(false);
  const [whitelistInput, setWhitelistInput] = useState('');
  const [fileAddresses, setFileAddresses] = useState<string[]>([]);
  const [whitelistLoading, setWhitelistLoading] = useState(false);

  // 🚫 Removed the side-effect that called /api/update-token-sale on mount.

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contract) return;
    if (chainId && chainId !== DESIRED_CHAIN_ID) {
      alert(t('Please switch to the supported network'));
      return;
    }

    const startTime = Math.floor(new Date(start).getTime() / 1000);
    const endTime = Math.floor(new Date(end).getTime() / 1000);
    const vestingStartTime = Math.floor(new Date(vestingStart).getTime() / 1000);
    const vestingCliffSec = BigInt(Number(cliff || '0') * 24 * 60 * 60);
    const vestingDurationSec = BigInt(Number(duration || '0') * 24 * 60 * 60);

    const priceValue = price.trim();
    if (priceValue === '' || isNaN(Number(priceValue))) {
      alert(t('Invalid price'));
      return;
    }

    const allocValue = alloc.trim();
    if (allocValue === '' || isNaN(Number(allocValue))) {
      alert(t('Invalid allocation'));
      return;
    }

    const nonKycValue = nonKyc.trim();
    if (nonKycValue === '' || isNaN(Number(nonKycValue))) {
      alert(t('Invalid Non-KYC cap'));
      return;
    }

    const priceUnits = ethers.parseUnits(priceValue, 6);
    const tokensPerUsdt = priceUnits === 0n ? 0n : (10n ** 24n) / priceUnits;

    const allocUnits = ethers.parseUnits(allocValue, 18);
    const nonKycUnits = ethers.parseUnits(nonKycValue, 18);
    const affiliatePercent = Number(affiliate || '0');

    if (endTime <= startTime) {
      alert(t('End time must be after start time'));
      return;
    }
    if (vestingStartTime < endTime) {
      alert(t('Vesting start must be after end time'));
      return;
    }
    if (vestingDurationSec === 0n || vestingCliffSec > vestingDurationSec) {
      alert(t('Invalid vesting schedule'));
      return;
    }
    if (tokensPerUsdt === 0n || allocUnits === 0n) {
      alert(t('Price and allocation must be greater than zero'));
      return;
    }
    if (affiliatePercent >= 100) {
      alert(t('Affiliate reward must be less than 100'));
      return;
    }

    try {
      setLoading(true);
      const tx = await contract.addRound(
        startTime,
        endTime,
        tokensPerUsdt,
        vestingStartTime,
        vestingCliffSec,
        vestingDurationSec,
        allocUnits,
        nonKycUnits,
        affiliatePercent
      );
      await tx.wait();
      alert(t('Round added successfully'));

      setStart('');
      setEnd('');
      setPrice('');
      setAlloc('');
      setNonKyc('');
      setVestingStart('');
      setCliff('');
      setDuration('');
      setAffiliate('');
    } catch (err: any) {
      console.error(err);
      const revertReason =
        err?.reason ||
        err?.data?.message ||
        err?.data ||
        err?.error?.reason ||
        err?.error?.message;
      const isRevert =
        err?.code === 'CALL_EXCEPTION' ||
        err?.error?.code === 'CALL_EXCEPTION' ||
        /revert/i.test(err?.message || '');
      if (isRevert) {
        alert(
          t('Transaction reverted or invalid. Please check your input constraints or contact support.') +
            (revertReason ? `\n${revertReason}` : '')
        );
      } else {
        alert(err?.message || String(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const addresses = text
        .split(/\r?\n/)
        .map((line) => line.split(',')[0].trim())
        .filter(Boolean);
      setFileAddresses(addresses);
    };
    reader.readAsText(file);
  };

  const handleWhitelistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contract) return;
    if (chainId && chainId !== DESIRED_CHAIN_ID) {
      alert(t('Please switch to the supported network'));
      return;
    }
    const inputAddresses = whitelistInput
      .split(',')
      .map((a) => a.trim())
      .filter(Boolean);
    const addresses = [...inputAddresses, ...fileAddresses];
    if (addresses.length === 0) {
      alert(t('No addresses provided'));
      return;
    }
    try {
      setWhitelistLoading(true);
      const tx = await contract.updateKycStatus(addresses, true);
      await tx.wait();
      alert(t('Whitelist updated successfully'));
      setWhitelistInput('');
      setFileAddresses([]);
    } catch (err) {
      console.error(err);
      alert(t('Unexpected error occurred'));
    } finally {
      setWhitelistLoading(false);
    }
  };

  if (address && chainId && chainId !== DESIRED_CHAIN_ID) {
    return (
      <Wrapper>
        <div className="container py-10 text-center">
          <p>⚠️ {t('Invtron DAO only supports the configured network.')}</p>
          <p>{t('Please switch to the supported network')}</p>
          <button
            onClick={() => {
              try {
                switchChain?.({ chainId: DESIRED_CHAIN_ID });
              } catch (err) {
                console.error('Failed to switch network', err);
                toast.error(t('Failed to switch network'));
              }
            }}
            className="tg-btn"
          >
            {t('Switch Network')}
          </button>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="container py-10">
        {!address ? (
          <button onClick={connect} disabled={connecting} className="tg-btn">
            {connecting ? t('Connecting...') : t('Connect Your Wallet')}
          </button>
        ) : (
          <>
            <div className="row justify-content-center">
              <div className="col-md-6">
                <form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow">
                  <h1 className="h4 text-center mb-4">{t('Add Round')}</h1>

                  <div className="mb-3">
                    <label className="form-label">{t('Start')}</label>
                    <input
                      type="datetime-local"
                      value={start}
                      onChange={(e) => setStart(e.target.value)}
                      required
                      className="form-control"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t('End')}</label>
                    <input
                      type="datetime-local"
                      value={end}
                      onChange={(e) => setEnd(e.target.value)}
                      required
                      className="form-control"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t('Price')} (USDT)</label>
                    <input
                      type="number"
                      step="any"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      required
                      className="form-control"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t('Allocated')}</label>
                    <input
                      type="number"
                      step="any"
                      value={alloc}
                      onChange={(e) => setAlloc(e.target.value)}
                      required
                      className="form-control"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t('Non-KYC Cap')}</label>
                    <input
                      type="number"
                      step="any"
                      value={nonKyc}
                      onChange={(e) => setNonKyc(e.target.value)}
                      required
                      className="form-control"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t('Vesting Start')}</label>
                    <input
                      type="datetime-local"
                      value={vestingStart}
                      onChange={(e) => setVestingStart(e.target.value)}
                      required
                      className="form-control"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t('Vesting Cliff (days)')}</label>
                    <input
                      type="number"
                      value={cliff}
                      onChange={(e) => setCliff(e.target.value)}
                      required
                      className="form-control"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">{t('Vesting Duration (days)')}</label>
                    <input
                      type="number"
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      required
                      className="form-control"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label">{t('Affiliate Reward (%)')}</label>
                    <input
                      type="number"
                      value={affiliate}
                      onChange={(e) => setAffiliate(e.target.value)}
                      required
                      className="form-control"
                    />
                  </div>

                  <button type="submit" className="tg-btn w-100" disabled={loading}>
                    {loading ? t('Processing...') : t('Add Round')}
                  </button>
                </form>
              </div>
            </div>

            <div className="row justify-content-center mt-5">
              <div className="col-md-6">
                <form onSubmit={handleWhitelistSubmit} className="bg-white p-4 rounded shadow">
                  <h1 className="h4 text-center mb-4">{t('Whitelist Users')}</h1>

                  <div className="mb-3">
                    <label className="form-label">{t('Addresses (comma separated)')}</label>
                    <textarea
                      value={whitelistInput}
                      onChange={(e) => setWhitelistInput(e.target.value)}
                      rows={3}
                      className="form-control"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label">{t('Upload CSV')}</label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="form-control"
                    />
                  </div>

                  {/* <button type="submit" className="tg-btn w-100" disabled={whitelistLoading}>
                    {whitelistLoading ? t('Processing...') : t('Whitelist')}
                  </button> */}
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </Wrapper>
  );
}

export default function AddRoundPage() {
  return (
    <Web3ModalProvider>
      <AddRoundPageContent />
    </Web3ModalProvider>
  );
}
